/**
 * Phase 5.9.10 — Shared Order Filter Bar.
 *
 * Used by both the super-admin and regional dispatcher ORDERS tabs. The
 * region select only renders when `isSuperAdmin` is true; the regional
 * dispatcher's filter bar is otherwise identical.
 *
 * Default state: collapsed. The single `FILTERS (N)` chip shows the active
 * filter count. One click expands; one click collapses. Reset button clears
 * all filters at once.
 *
 * Filter shape (controlled by parent via `filters` + `onChange`):
 *   {
 *     status: string[],         // multi-select (e.g. ["pending","preparing"])
 *     driverId: string|null,    // single driver scope, or "unassigned", or null
 *     dateRange: string,        // "all" | "today" | "7d" | "30d"
 *     search: string,           // free-text — matched against id, name, phone, address
 *   }
 *
 * The region filter is owned by RegionContext (selectedRegion) and not
 * exposed here — keeps a single source of truth for the SA region switcher.
 */
import { useMemo, useState } from 'react';
import {
  Filter, X, Search, Calendar, User, Globe, ChevronDown, ChevronUp, RotateCcw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

export const STATUS_OPTIONS = [
  { value: 'pending',           label: 'PENDING',       color: '#FF9F0A' },
  { value: 'confirmed',         label: 'CONFIRMED',     color: '#39FF14' },
  { value: 'preparing',         label: 'PREPARING',     color: '#FFD700' },
  { value: 'out_for_delivery',  label: 'OUT',           color: '#7FD8FF' },
  { value: 'delivered',         label: 'DELIVERED',     color: '#39FF14' },
  { value: 'cancelled',         label: 'CANCELLED',     color: '#FF3B30' },
];

const DATE_OPTIONS = [
  { value: 'all',   label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d',    label: 'Last 7 days' },
  { value: '30d',   label: 'Last 30 days' },
];

export const EMPTY_FILTERS = {
  status: [],
  driverId: null,
  dateRange: 'all',
  search: '',
};

export function countActiveFilters(filters) {
  if (!filters) return 0;
  let n = 0;
  if (filters.status && filters.status.length > 0) n += 1;
  if (filters.driverId) n += 1;
  if (filters.dateRange && filters.dateRange !== 'all') n += 1;
  if (filters.search && filters.search.trim().length > 0) n += 1;
  return n;
}

export function OrderFiltersBar({
  filters,
  onChange,
  drivers = [],
  resultCount = 0,
  // Phase 5.9.12 — region filter for SA, synced with RegionContext.
  // For regional dispatcher, both props are ignored (region select is hidden).
  isSuperAdmin = false,
  selectedRegion = 'all',
  onRegionChange,
}) {
  const [expanded, setExpanded] = useState(false);
  const regionActive = isSuperAdmin && selectedRegion && selectedRegion !== 'all';
  const activeCount = countActiveFilters(filters) + (regionActive ? 1 : 0);

  const driverOptions = useMemo(() => {
    return drivers.map((d) => ({ value: d.id, label: d.name || d.email || d.id.slice(0, 8) }));
  }, [drivers]);

  const toggleStatus = (value) => {
    const current = filters.status || [];
    const next = current.includes(value)
      ? current.filter((s) => s !== value)
      : [...current, value];
    onChange({ ...filters, status: next });
  };

  const clearAll = () => {
    onChange({ ...EMPTY_FILTERS });
    // Also reset the region for SA — gives a true "everything to defaults"
    // RESET. The header RegionBadge updates automatically (single source).
    if (isSuperAdmin && onRegionChange) onRegionChange('all');
  };

  return (
    <div className="bg-[#0C0612] border border-[#2A0F3D] rounded-none" data-testid="order-filters-bar">
      {/* Collapsed header row — always visible */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          className={[
            'rounded-none font-mono text-xs h-8 px-2',
            activeCount > 0
              ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.06)]'
              : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14]',
          ].join(' ')}
          data-testid="order-filters-toggle"
          aria-expanded={expanded}
        >
          <Filter className="w-3.5 h-3.5 mr-1.5" />
          FILTERS
          {activeCount > 0 && (
            <span
              className="ml-1.5 px-1.5 py-0.5 bg-[#39FF14] text-[#050003] font-bold text-[10px] rounded-sm"
              data-testid="order-filters-count"
            >
              {activeCount}
            </span>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
        </Button>

        {/* Quick search — always visible even when filter panel collapsed */}
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4B6B52] pointer-events-none" />
            <Input
              type="search"
              value={filters.search || ''}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              placeholder="Search order ID, customer, phone, address…"
              className="pl-7 h-8 text-xs font-mono bg-black/40 border-[#2A0F3D] rounded-none focus-visible:ring-[#39FF14]"
              data-testid="order-filters-search"
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => onChange({ ...filters, search: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#39FF14]"
                aria-label="Clear search"
                data-testid="order-filters-search-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <span className="text-[#6B8E73] font-mono text-xs whitespace-nowrap" data-testid="order-filters-result-count">
            {resultCount} {resultCount === 1 ? 'order' : 'orders'}
          </span>
        </div>

        {/* Reset (only when filters active) */}
        {activeCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearAll}
            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.08)] rounded-none font-mono text-[10px] h-8 px-2"
            data-testid="order-filters-clear-all"
            title="Reset all filters"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            RESET
          </Button>
        )}
      </div>

      {/* Expanded filter panel */}
      {expanded && (
        <div className="border-t border-[#2A0F3D] p-3 space-y-3" data-testid="order-filters-expanded">
          {/* Status chips */}
          <div>
            <div className="text-[10px] uppercase text-[#6B8E73] font-mono mb-1.5 tracking-wider">
              // Status
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const active = (filters.status || []).includes(opt.value);
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => toggleStatus(opt.value)}
                    className="px-2 py-1 rounded-none border font-mono text-[10px] transition-colors"
                    style={{
                      borderColor: active ? opt.color : '#2A0F3D',
                      color: active ? '#050003' : opt.color,
                      background: active ? opt.color : 'transparent',
                    }}
                    data-testid={`order-filters-status-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Region (super-admin only — synced with the header RegionBadge) */}
          {isSuperAdmin && (
            <div data-testid="order-filters-region-wrapper">
              <div className="text-[10px] uppercase text-[#6B8E73] font-mono mb-1.5 tracking-wider">
                <Globe className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                Region
              </div>
              <Select
                value={selectedRegion || 'all'}
                onValueChange={(v) => onRegionChange?.(v)}
              >
                <SelectTrigger
                  className="h-8 text-xs font-mono bg-black/40 border-[#2A0F3D] rounded-none"
                  data-testid="order-filters-region"
                >
                  <SelectValue placeholder="All regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  <SelectItem value="dmv">DMV</SelectItem>
                  <SelectItem value="nj">New Jersey</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[9px] text-[#4B6B52] mt-1 font-mono">
                // synced with header
              </p>
            </div>
          )}

          {/* Driver + Date selects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase text-[#6B8E73] font-mono mb-1.5 tracking-wider">
                <User className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                Driver
              </div>
              <Select
                value={filters.driverId || 'all'}
                onValueChange={(v) =>
                  onChange({ ...filters, driverId: v === 'all' ? null : v })
                }
              >
                <SelectTrigger
                  className="h-8 text-xs font-mono bg-black/40 border-[#2A0F3D] rounded-none"
                  data-testid="order-filters-driver"
                >
                  <SelectValue placeholder="All drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drivers</SelectItem>
                  <SelectItem value="unassigned">— Unassigned —</SelectItem>
                  {driverOptions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="text-[10px] uppercase text-[#6B8E73] font-mono mb-1.5 tracking-wider">
                <Calendar className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                Date placed
              </div>
              <Select
                value={filters.dateRange || 'all'}
                onValueChange={(v) => onChange({ ...filters, dateRange: v })}
              >
                <SelectTrigger
                  className="h-8 text-xs font-mono bg-black/40 border-[#2A0F3D] rounded-none"
                  data-testid="order-filters-daterange"
                >
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Pure client-side filter applier. Returns a new array of orders that match
 * the filter set. Region scope is already applied by the parent before this
 * runs (via the existing RegionContext / filterByRegionScope helper).
 *
 * Search is case-insensitive across: short order id, customer name,
 * customer phone, delivery address.
 */
export function applyOrderFilters(orders, filters) {
  if (!filters || countActiveFilters(filters) === 0) return orders;
  const statusSet = new Set(filters.status || []);
  const wantsDriver = filters.driverId;
  const range = filters.dateRange || 'all';
  const search = (filters.search || '').trim().toLowerCase();

  let cutoff = null;
  if (range !== 'all') {
    const now = new Date();
    if (range === 'today') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    } else if (range === '7d') {
      cutoff = now.getTime() - 7 * 24 * 60 * 60 * 1000;
    } else if (range === '30d') {
      cutoff = now.getTime() - 30 * 24 * 60 * 60 * 1000;
    }
  }

  return orders.filter((o) => {
    if (statusSet.size > 0 && !statusSet.has(o.status)) return false;

    if (wantsDriver === 'unassigned') {
      if (o.driver_id) return false;
    } else if (wantsDriver) {
      if (o.driver_id !== wantsDriver) return false;
    }

    if (cutoff !== null) {
      const createdMs = new Date(o.created_at).getTime();
      if (Number.isNaN(createdMs) || createdMs < cutoff) return false;
    }

    if (search) {
      const haystack = [
        (o.id || '').slice(0, 8).toLowerCase(),
        (o.customer_name || '').toLowerCase(),
        (o.customer_phone || '').toLowerCase(),
        (o.delivery_address || '').toLowerCase(),
      ].join(' | ');
      if (!haystack.includes(search)) return false;
    }

    return true;
  });
}
